import React, { useMemo, useState } from "react";
import TreatmentModal from "./TreatmentModal";
import {
  buildTreatmentSummaryParts,
  createBlankTreatment,
  getDefaultTreatmentType,
} from "./treatmentUtils";

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#166534",
  color: "#fff",
  borderColor: "#166534",
};

const dangerButtonStyle = {
  ...buttonStyle,
  color: "#b91c1c",
  borderColor: "#fecaca",
  background: "#fff",
};

function formatDisplayTimestamp(value = "") {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export default function TreatmentSection({
  treatments = [],
  setTreatments,
  employeeOptions = [],
  defaultPerformedBy = "",
  categoryFilter = "",
  defaultCategory = "",
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [draftTreatment, setDraftTreatment] = useState(createBlankTreatment());

  const sortedTreatments = useMemo(() => {
  if (!categoryFilter) return treatments || [];

  return (treatments || []).filter(
    (treatment) => treatment.category === categoryFilter
  );
}, [treatments, categoryFilter]);

  function openAddModal() {
  setEditingIndex(-1);

  const blank = defaultCategory
    ? createBlankTreatment(getDefaultTreatmentType(defaultCategory))
    : createBlankTreatment();

  setDraftTreatment({
    ...blank,
    category: defaultCategory || blank.category,
    performedBy: defaultPerformedBy || "",
  });

  setIsModalOpen(true);
}

  function openEditModal(index) {
    setEditingIndex(index);
    setDraftTreatment(treatments[index]);
    setIsModalOpen(true);
  }

  function handleDelete(index) {
    setTreatments((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSaveTreatment(nextTreatment) {
    setTreatments((prev) => {
      if (editingIndex >= 0) {
        return prev.map((item, index) => (index === editingIndex ? nextTreatment : item));
      }
      return [...prev, nextTreatment];
    });

    setIsModalOpen(false);
    setEditingIndex(-1);
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, color: "#334155", fontWeight: 600 }}>
         {defaultCategory || "Entries"}
        </div>

        <button type="button" style={primaryButtonStyle} onClick={openAddModal}>
  + Add {defaultCategory || "Treatment"}
</button>
      </div>

      {!sortedTreatments.length ? (
        <div
          style={{
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            padding: 14,
            color: "#64748b",
            background: "#f8fafc",
          }}
        >
         No {defaultCategory ? `${defaultCategory.toLowerCase()}s` : "entries"} added yet.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {sortedTreatments.map((treatment, index) => {
            const summaryParts = buildTreatmentSummaryParts(treatment);

            return (
              <div
                key={treatment.id || `${treatment.type}-${index}`}
                style={{
                  border: "1px solid #d1fae5",
                  borderRadius: 12,
                  padding: 10,
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span style={{ fontWeight: 800, color: "#166534" }}>{treatment.type}</span>

                  {treatment.timestamp && (
                    <>
                      <span style={{ color: "#94a3b8" }}>|</span>
                      <span>{formatDisplayTimestamp(treatment.timestamp)}</span>
                    </>
                  )}

                  {treatment.performedBy && (
                    <>
                      <span style={{ color: "#94a3b8" }}>|</span>
                      <span>{treatment.performedBy}</span>
                    </>
                  )}

                  {summaryParts.map((part, idx) => (
                    <React.Fragment key={`${part}-${idx}`}>
                      <span style={{ color: "#94a3b8" }}>|</span>
                      <span>{part}</span>
                    </React.Fragment>
                  ))}

                  {treatment.successful && (
                    <>
                      <span style={{ color: "#94a3b8" }}>|</span>
                      <span
                        style={{
                          fontWeight: 700,
                          color: treatment.successful === "Yes" ? "#166534" : "#b91c1c",
                        }}
                      >
                        {treatment.successful === "Yes" ? "Successful" : "Not Successful"}
                      </span>
                    </>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" style={buttonStyle} onClick={() => openEditModal(index)}>
                    Edit
                  </button>
                  <button type="button" style={dangerButtonStyle} onClick={() => handleDelete(index)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TreatmentModal
        isOpen={isModalOpen}
        initialTreatment={draftTreatment}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTreatment}
        employeeOptions={employeeOptions}
      />
    </div>
  );
}